import React, { useState, useRef, useEffect } from 'react';
import { validateTicket } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { QrCode, Camera, CheckCircle, XCircle, AlertTriangle, Keyboard } from 'lucide-react';

const Scanner = () => {
  const { user } = useAuth();
  const [ticketId, setTicketId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus input on mount for barcode scanner support
    inputRef.current?.focus();
    
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const handleValidate = async (id = ticketId) => {
    if (!id.trim()) {
      toast.error('Ingresa un ID de ticket');
      return;
    }

    // Extract ticket ID if it's a QR data string
    let extractedId = id;
    if (id.startsWith('MASTERTICKETS:')) {
      const parts = id.split(':');
      extractedId = parts[1] || id;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await validateTicket(extractedId);
      setResult(res.data);
      
      if (res.data.valid) {
        toast.success('¡Ticket válido!');
      } else {
        toast.error(res.data.message || 'Ticket no válido');
      }
    } catch (error) {
      console.error('Validation error:', error);
      setResult({ valid: false, message: error.response?.data?.detail || 'Error al validar' });
      toast.error('Error al validar ticket');
    } finally {
      setLoading(false);
      setTicketId('');
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleValidate();
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      setCameraStream(stream);
      setScanning(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('No se pudo acceder a la cámara');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setScanning(false);
  };

  const ResultCard = ({ result }) => {
    if (!result) return null;

    return (
      <div className={`glass-card p-6 ${result.valid ? 'border-secondary' : 'border-destructive'}`}>
        <div className="flex items-center gap-4 mb-4">
          {result.valid ? (
            <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-secondary" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
          )}
          <div>
            <h3 className="text-xl font-bold">
              {result.valid ? '¡Acceso Permitido!' : 'Acceso Denegado'}
            </h3>
            <p className="text-muted-foreground">{result.message}</p>
          </div>
        </div>

        {result.ticket && (
          <div className="space-y-2 pt-4 border-t border-border">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Evento:</span>
              <span className="font-medium">{result.ticket.event_title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tipo:</span>
              <span className="font-medium">{result.ticket.ticket_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Asistente:</span>
              <span className="font-medium">{result.ticket.user_name}</span>
            </div>
            {result.used_at && (
              <div className="flex justify-between text-yellow-500">
                <span>Usado:</span>
                <span>{new Date(result.used_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="scanner-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <QrCode className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">Validar Tickets</h1>
          <p className="text-muted-foreground">
            Escanea o ingresa el código del ticket
          </p>
        </div>

        {/* Manual Input */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Keyboard className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Entrada Manual / Scanner</span>
          </div>
          
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ID del ticket o código QR"
              className="flex-1"
              data-testid="ticket-id-input"
            />
            <Button
              onClick={() => handleValidate()}
              disabled={loading}
              className="btn-primary"
              data-testid="validate-button"
            >
              {loading ? 'Validando...' : 'Validar'}
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-2">
            Usa un scanner de código de barras o ingresa el ID manualmente
          </p>
        </div>

        {/* Camera Scanner */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Camera className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Cámara</span>
          </div>

          {scanning ? (
            <div className="space-y-4">
              <div className="relative aspect-square bg-black rounded-xl overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 border-2 border-primary/50 rounded-xl" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-primary rounded-lg" />
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={stopCamera}
              >
                Detener Cámara
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Nota: La detección automática de QR requiere una librería adicional.
                Por ahora, usa el input manual.
              </p>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              onClick={startCamera}
            >
              <Camera className="w-4 h-4 mr-2" />
              Activar Cámara
            </Button>
          )}
        </div>

        {/* Result */}
        <ResultCard result={result} />

        {/* Quick Stats */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Rol: {user?.role}</p>
        </div>
      </div>
    </div>
  );
};

export default Scanner;
